import { ActionType } from "./model";
import type {
  LoginUserPayload,
  LoginUserData,
  VerifyUserData,
  VerifyUserPayload,
  LoginShopifyUserPayload
} from "./model";

import { createApiCall } from "services";
import type { ActionCallback } from "store/model";
import { isLocalhost, storage } from "utils";

import type { AxiosResponse } from "axios";
import { fork, put, call, takeLatest } from "redux-saga/effects";
import { GoogleLoginPayload } from "./types";
import { googleLoginFailure, googleLoginSuccess } from "./actions";

export function* getOtpCodeSaga(): Generator {
  yield takeLatest(
    ActionType.GET_OTP_CODE,
    function* (action: ActionCallback<LoginUserPayload>) {
      try {
        const { payload, callback } = action;

        const response: AxiosResponse<LoginUserData> = yield call(
          createApiCall,
          {
            method: "POST",
            url: "/auth/customer/login",
            data: payload,
          }
        );

        const { data, status } = response;

        if (status === 201) {
          yield put({
            type: ActionType.SET_OTP_CODE,
            payload: data.otpReference,
          });
        }

        if (callback) {
          yield callback({ success: status === 201 });
        }
      } catch (e) {
        console.error(e);
      }
    }
  );
}

export function* loginUserSaga(): Generator {
  yield takeLatest(
    ActionType.LOGIN_USER,
    function* (action: ActionCallback<VerifyUserPayload>) {
      const { payload, callback } = action;
      try {
        const response: AxiosResponse<VerifyUserData> = yield call(
          createApiCall,
          {
            method: "POST",
            url: "/auth/customer/verify",
            data: payload,
          }
        );

        const { data, status } = response;

        if (status === 201 && data) {
          const { access_token, refresh_token, scan_token } = data;

          storage.setItem("accessToken", access_token);
          storage.setItem("refreshToken", refresh_token);
          storage.setItem("scanToken", scan_token);

          yield put({
            type: ActionType.SET_AUTH,
            payload: { isAuthenticated: true },
          });

          yield put({
            type: ActionType.GET_PROFILE,
          });
        }

        if (callback) {
          yield callback({ success: status === 201 });
        }
      } catch (e) {
        console.error(e);

        if (callback) {
          yield callback({ success: false, text: "Something went wrong!" });
        }
      }
    }
  );
}

export function* loginShopifyUserSaga(): Generator {
  yield takeLatest(
    ActionType.LOGIN_SHOPIFY_USER,
    function* (action: ActionCallback<LoginShopifyUserPayload>) {
      const { payload, callback } = action;
      try {
        const { data, status } = yield call(createApiCall, {
          method: "GET",
          url: "/auth/customer/shopify-login",
          headers: {
            shopify_token: payload.shopify_token,
          },
        });

        if (status === 200) {
          const { access_token, refresh_token, scan_token } = data;

          storage.setItem("accessToken", access_token);
          storage.setItem("refreshToken", refresh_token);
          storage.setItem("scanToken", scan_token);

          yield put({
            type: ActionType.SET_AUTH,
            payload: { isAuthenticated: true },
          });

          yield put({
            type: ActionType.GET_PROFILE,
          });
        }

        if (callback) {
          // VERA-838: the endpoint responds 200 (GET), not 201 — this was
          // checking the wrong status, so the callback always reported
          // failure even when the token exchange (and storage.setItem above)
          // succeeded. Callers relying on the callback's `success` flag
          // (e.g. apps/app/src/App.tsx) would silently misbehave: tokens
          // were stored, but the "logged in" branch never ran.
          callback({ success: status === 200 });
        }
      } catch (e) {
        console.error(e);

        if (callback) {
          yield callback({ success: false, text: "Something went wrong!" });
        }
      }
    }
  );
}

export function* accountLinkCheckSaga(): Generator {
  yield takeLatest(
    ActionType.ACCOUNT_LINK_CHECK,
    function* (action: ActionCallback) {
      try {
        const { payload, callback } = action;

        const { data, status } = yield call(createApiCall, {
          method: "POST",
          url: "/account-link/check",
          data: payload,
        });

        if (status === 201) {
          yield put({
            type: ActionType.SET_ACCOUNT_LINK,
            payload: data,
          });
        }

        if (callback) {
          callback({ success: status === 201 });
        }
      } catch (e) {
        console.warn(e);
      }
    }
  );
}

export function* linkAccountsSaga(): Generator {
  yield takeLatest(
    ActionType.LINK_ACCOUNTS,
    function* (action: ActionCallback) {
      try {
        const { payload, callback } = action;

        const { data, status } = yield call(createApiCall, {
          method: "POST",
          url: "/account-link/link",
          data: payload,
        });

        if (status === 201) {
          yield put({
            type: ActionType.SET_OTP_CODE,
            payload: data.otpReference,
          });
        }

        if (callback) {
          yield callback({ success: status === 201 });
        }
      } catch (e) {
        console.warn(e);
      }
    }
  );
}

export function* accountLinkConfirmSaga(): Generator {
  yield takeLatest(
    ActionType.ACCOUNT_LINK_CONFIRM,
    function* (action: ActionCallback) {
      try {
        const { payload, callback } = action;

        const { data, status } = yield call(createApiCall, {
          method: "POST",
          url: "/account-link/confirm",
          data: payload,
        });

        if (status === 200) {
          yield put({
            type: ActionType.SET_ACCOUNT_LINK,
            payload: data,
          });
        }

        if (callback) {
          yield callback({ success: status === 201 });
        }
      } catch (e) {
        console.warn(e);
      }
    }
  );
}

export function* getProfileSaga(): Generator {
  yield takeLatest(ActionType.GET_PROFILE, function* (action: ActionCallback) {
    try {
      const { payload, callback } = action;

      const { data, status } = yield call(createApiCall, {
        method: "GET",
        url: "/auth/customer/profile",
        params: payload,
      });

      if (status === 200) {
        yield put({
          type: ActionType.SET_PROFILE,
          payload: data,
        });
      }

      if (callback) callback({ success: status === 200 });
    } catch (e) {
      console.warn(e);
    }
  });
}

export function* updateProfileSaga(): Generator {
  yield takeLatest(
    ActionType.UPDATE_PROFILE,
    function* (action: ActionCallback) {
      try {
        const { payload, callback } = action;
        const { avatar, ...rest } = payload;

        let avatarImage: string | null = null;

        if (avatar instanceof File) {
          const { data, status } = yield uploadImage(avatar);

          if (status === 201) {
            avatarImage = data.path;
          }
        } else if (typeof avatar === "string") {
          avatarImage = avatar;
        }

        const { data, status } = yield call(createApiCall, {
          method: "POST",
          url: "/auth/customer/profile",
          data: {
            avatar: avatarImage,
            ...rest,
          },
        });

        if (status === 201) {
          yield put({
            type: ActionType.SET_PROFILE,
            payload: data,
          });
        }

        if (callback) callback({ success: status === 201, content: data });
      } catch (e) {
        console.error(e);
      }
    }
  );
}

/**
 * Dev-only owner auto-login (VRG-107 §A, under VERA-367-GCP).
 *
 * Mints a CUSTOMER/OWNER token set for the seeded certificate owner so the
 * CertificatePage renders as the OWNER without the WorkOS magic-auth round trip —
 * the customer-side mirror of the platform's `/auth/dev/session` bypass. Gated on
 * `VITE_DEV_SESSION` + localhost; the API additionally rejects unless
 * `NODE_ENV !== 'production'` and `DEV_SESSION_SECRET` matches. Returns false (no-op)
 * on any failure so the normal magic-auth flow still applies.
 */
/**
 * Is this JWT past its `exp`?
 *
 * Deliberately permissive: an unreadable or `exp`-less token counts as NOT
 * expired, so a malformed value can never put the app into a mint loop. The
 * server remains the authority — this only decides whether the DEV bootstrap
 * bothers to re-mint.
 */
function isExpired(token: string | null): boolean {
  if (!token) return false;
  try {
    const [, payload] = token.split('.');
    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof exp === 'number' && exp * 1000 <= Date.now();
  } catch {
    return false;
  }
}

async function bootstrapDevCustomerSession(): Promise<boolean> {
  if (!import.meta.env.VITE_DEV_SESSION || !isLocalhost()) return false;

  try {
    const { data, status } = await createApiCall({
      method: "POST",
      url: "/auth/dev/customer-session",
      data: { secret: import.meta.env.VITE_DEV_SESSION_SECRET },
    });

    if ((status === 200 || status === 201) && data?.access_token) {
      storage.setItem("accessToken", data.access_token);
      storage.setItem("refreshToken", data.refresh_token);
      storage.setItem("scanToken", data.scan_token);
      return true;
    }
  } catch (e) {
    console.warn("[dev-session] customer bootstrap failed", e);
  }
  return false;
}

function* checkAuthSaga() {
  yield takeLatest(ActionType.CHECK_AUTH, function* (action: ActionCallback) {
    try {
      const { callback } = action;

      let accessToken = storage.getItem("accessToken");
      let refreshToken = storage.getItem("refreshToken");
      let scanToken = storage.getItem("scanToken");

      // Dev-only owner auto-login: when tokens are absent OR the access token has
      // EXPIRED, and VITE_DEV_SESSION is on. Re-read from storage after a mint.
      //
      // The expiry half is why this is not just "absent". The check used to be
      // `!(accessToken && refreshToken && scanToken)`, so a preview left open past
      // the token's lifetime kept three present-but-dead tokens, skipped the mint,
      // and sent an expired token on every request forever. The visible result was
      // the certificates list stuck on "Forbidden resource" — NestJS's built-in
      // ForbiddenException text, surfaced when AuthCustomerGuard rejects — beside a
      // "Try again" button that re-sent the same dead token. The only escape was
      // clearing localStorage by hand, which nothing in the UI tells you to do.
      // Measured: token expired 43 minutes before the page was still showing it.
      if (!(accessToken && refreshToken && scanToken) || isExpired(accessToken)) {
        const minted: boolean = yield call(bootstrapDevCustomerSession);
        if (minted) {
          accessToken = storage.getItem("accessToken");
          refreshToken = storage.getItem("refreshToken");
          scanToken = storage.getItem("scanToken");
        }
      }

      const isTokens = accessToken && refreshToken && scanToken;

      if (isTokens) {
        yield put({ type: ActionType.SET_AUTH, payload: true });
        yield put({ type: ActionType.GET_PROFILE });
      } else {
        yield put({ type: ActionType.SET_AUTH, payload: false });
      }

      if (callback) callback({ success: !!isTokens });
    } catch (error) {
      console.error(error);
    }
  });
}

function* uploadImage(file: File): Generator {
  try {
    const form = new FormData();
    form.append("file", file);
    const response: any = yield call(createApiCall, {
      method: "POST",
      url: `/media/customer/avatar`,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: form,
    });
    return response;
  } catch (e) {
    console.error(e);
  }
}

function* handleGoogleLogin(): Generator {
  yield takeLatest(ActionType.GOOGLE_LOGIN_REQUEST, function* (action: ActionCallback<GoogleLoginPayload>) {
    try {
      const { data, status } = yield call(createApiCall, {
        method: 'POST',
        url: '/auth/customer/sso-login',
        data: {
          provider_type: 'google',
          provider_id: action.payload.credential
        },
      })

      if (status === 201 && data) {
        const { access_token, refresh_token } = data

        yield put(googleLoginSuccess(data))

        storage.setItem('accessToken', access_token)
        storage.setItem('refreshToken', refresh_token)

        yield put({
          type: ActionType.SET_AUTH,
          payload: true,
        })
      }
    } catch (error) {
      console.error('saga.ts', '❌', 19, 'handleGoogleLogin', 'error', error)
      yield put(googleLoginFailure(error as Error))
    }
  })
}

export default [
  fork(getOtpCodeSaga),
  fork(loginUserSaga),
  fork(accountLinkCheckSaga),
  fork(linkAccountsSaga),
  fork(accountLinkConfirmSaga),
  fork(getProfileSaga),
  fork(updateProfileSaga),
  fork(checkAuthSaga),
  fork(loginShopifyUserSaga),
  fork(handleGoogleLogin),
];
